// The prize dial after a registration has been written.
//
// NOT A STAGE IN flow.ts. The transition table stays as it is: register still
// ends at `done`. This surface sits on that ending when the stand's join kind
// is wheel and the 201 carried a spin ticket. The service picks the segment;
// the dial only animates to the index it is handed.

import { useMemo, useState, type ReactNode } from "react";
import {
  Cta,
  SHIPPED_SYSTEM,
  Wheel,
  WheelLogo,
  WHEEL_SPIN_MS,
  segmentFromLabel,
  type VisitorLogo,
  type WheelLane,
} from "@nova/xp-visitor";
import { HomeReset, RESULT_RESET_MS } from "../../ui/src/home/HomeReset.js";
import { spinWheel, type Refusal, type Transport } from "./flow.js";
import type { PageTheme } from "./theme.js";

export function WheelAfter({
  activationId,
  ticket,
  slices,
  lane,
  mode,
  transport,
  baseUrl,
  logo,
  crewResetEnabled = true,
  onHome,
}: {
  readonly activationId: string;
  readonly ticket: string;
  readonly slices: readonly { readonly id: string; readonly label: string }[];
  readonly lane: WheelLane;
  readonly mode: PageTheme;
  readonly transport: Transport;
  readonly baseUrl: string;
  readonly logo?: VisitorLogo;
  readonly crewResetEnabled?: boolean;
  readonly onHome: () => void;
}): ReactNode {
  const [note, setNote] = useState<Refusal | null>(null);
  const [spinning, setSpinning] = useState(false);
  const [landed, setLanded] = useState<number | null>(null);
  const [claim, setClaim] = useState<string | null>(null);
  const [prize, setPrize] = useState<string | null>(null);
  const [won, setWon] = useState(false);
  const [spent, setSpent] = useState(false);
  const segments = useMemo(() => {
    const rows = slices.length >= 4 ? slices : Array.from({ length: 8 }, (_, i) => ({ id: `s${i + 1}`, label: "" }));
    return rows.map((row) => segmentFromLabel(row.id, row.label));
  }, [slices]);

  const runSpin = async (): Promise<void> => {
    if (spent || spinning) return;
    setNote(null);
    setSpinning(true);
    const outcome = await spinWheel(transport, baseUrl, {
      activation_id: activationId,
      ticket,
    });
    if (!outcome.ok) {
      setSpinning(false);
      setNote(outcome.refusal);
      return;
    }
    setLanded(outcome.landed.index);
    window.setTimeout(() => {
      setSpinning(false);
      setClaim(outcome.landed.claim);
      setPrize(outcome.landed.label);
      setWon(outcome.landed.prize);
      setSpent(true);
    }, WHEEL_SPIN_MS);
  };

  const shown = spent;

  return (
    <div className="web-wheel">
      <Wheel
        lane={lane}
        system={SHIPPED_SYSTEM}
        mode={mode}
        segments={segments}
        showLabels
        showFooter={false}
        interactive={!spent && !spinning}
        spinning={spinning}
        landed={landed}
        {...(logo === undefined ? {} : { logo })}
        onSpin={() => void runSpin()}
        copyColumn={
          shown ? (
            <div className="col">
              <WheelLogo logo={logo ?? { src: null, name: "Nova" }} />
              {won ? (
                <div className="won">
                  <p className="small">You won</p>
                  <h1 className="prize">{prize}</h1>
                  {claim !== null && claim !== "" ? (
                    <div className="claim">
                      <span className="claim-l">Show this at the stand</span>
                      <span className="claim-v">{claim}</span>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="won">
                  <h1 className="prize">Not this time</h1>
                  <p className="body">That segment was a blank. Staff can still help at the stand.</p>
                </div>
              )}
              {note === null ? null : (
                <p className="small" role="alert">
                  {note.problem}
                </p>
              )}
              <Cta ready onClick={onHome}>
                Finish
              </Cta>
              {/* Onsite only, and deliberately. A hall screen has a queue behind
                  it, so it has to clear itself. A visitor's own phone is showing
                  a claim code they were told to quote at the stand, and a timer
                  that reloads it takes the prize away from them. They finish
                  when they say so. */}
        {lane === "onsite" ? (
          <HomeReset
            onHome={onHome}
            afterMs={RESULT_RESET_MS}
            crewResetEnabled={crewResetEnabled}
          />
        ) : null}
            </div>
          ) : (
            <div className="col">
              <WheelLogo logo={logo ?? { src: null, name: "Nova" }} />
              <h1 className="head">{spinning ? "Good luck" : "Spin the wheel to\nwin a prize"}</h1>
              <p className="body">
                {spinning
                  ? "The wheel is slowing down. Whatever it stops on is yours."
                  : "Press spin, or give the wheel a flick. One spin per person."}
              </p>
              {note === null ? null : (
                <p className="small" role="alert">
                  {note.problem}
                </p>
              )}
              <Cta ready={!spinning} disabled={spinning} onClick={() => void runSpin()}>
                {spinning ? "Spinning…" : "Spin"}
              </Cta>
            </div>
          )
        }
      />
    </div>
  );
}
